import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import PageLayout from '@/components/feature/PageLayout';

const springTransition = { type: 'spring' as const, stiffness: 80, damping: 20 };

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content:
      'By accessing or using the Thoughtic Solutions website (thoughtic.com) and any related services, you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our website or services. These terms apply to all visitors, users, clients, and others who access or use our services.',
  },
  {
    id: 'services',
    title: '2. Services Description',
    content:
      'Thoughtic Solutions provides custom software development, IT staffing, staff augmentation, dedicated development teams, and related consulting services. All services are delivered pursuant to separate master service agreements, statements of work, or engagement letters executed between Thoughtic Solutions and the client. These Terms of Use govern your use of our website and do not modify any separately executed agreement.',
  },
  {
    id: 'ip',
    title: '3. Intellectual Property Rights',
    content:
      'All content on this website — including text, graphics, logos, icons, images, audio clips, digital downloads, data compilations, and software — is the property of Thoughtic Solutions or its content suppliers and is protected by applicable copyright, trademark, and intellectual property laws. You may not reproduce, distribute, modify, create derivative works of, publicly display, or otherwise exploit any content without our prior written consent. For client project deliverables, intellectual property ownership is governed by the applicable master service agreement — clients retain full ownership of all work produced for them.',
  },
  {
    id: 'client-obligations',
    title: '4. Client Obligations',
    content:
      'When engaging our services, clients agree to: (a) provide accurate and complete information necessary for project execution; (b) cooperate reasonably with our team, including timely provision of access, materials, and feedback; (c) maintain the confidentiality of any proprietary information shared during the engagement; (d) comply with all applicable laws and regulations in connection with the services received.',
  },
  {
    id: 'payment',
    title: '5. Payment Terms',
    content:
      'Payment terms are specified in each individual engagement agreement. Generally, we bill monthly in arrears with net-15 payment terms. For long-term engagements, net-30 terms may be arranged upon request. All invoices include detailed breakdowns by resource, hours worked, and any approved expenses. Late payments may incur interest charges as specified in the applicable agreement. We accept wire transfers, ACH, and major credit cards.',
  },
  {
    id: 'confidentiality',
    title: '6. Confidentiality',
    content:
      'Thoughtic Solutions treats all client information as confidential. We sign non-disclosure agreements before any engagement begins. Our engineers sign individual NDAs and IP assignment agreements. We implement industry-standard security measures to protect your proprietary information, including encrypted communications, access controls, and secure development practices. Detailed security information is available on our Trust & Security page.',
  },
  {
    id: 'limitation',
    title: '7. Limitation of Liability',
    content:
      'To the fullest extent permitted by applicable law, Thoughtic Solutions shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation loss of profits, data, use, goodwill, or other intangible losses, resulting from: (a) your use or inability to use our website or services; (b) any conduct or content of any third party on the website; (c) any content obtained from the website; or (d) unauthorized access, use, or alteration of your transmissions or content. Our total liability for any claims under these terms is limited to the amount you paid us for the applicable service in the 12 months preceding the claim.',
  },
  {
    id: 'termination',
    title: '8. Termination',
    content:
      'We may terminate or suspend access to our website immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms. For client engagements, termination provisions are governed by the applicable master service agreement. Upon termination of an engagement, clients receive full access to all deliverables, source code, and documentation produced during the engagement.',
  },
  {
    id: 'governing-law',
    title: '9. Governing Law',
    content:
      'These Terms shall be governed and construed in accordance with the laws of India, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts located in India. For international clients, the governing law and dispute resolution mechanism may be mutually agreed upon in the applicable engagement agreement.',
  },
  {
    id: 'changes',
    title: '10. Changes to Terms',
    content:
      'We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days\' notice before any new terms take effect. What constitutes a material change will be determined at our sole discretion. By continuing to access or use our website after those revisions become effective, you agree to be bound by the revised terms.',
  },
  {
    id: 'contact',
    title: '11. Contact Information',
    content:
      'If you have any questions about these Terms of Use, please contact us at:',
    contact: true,
  },
];

function PageHero() {
  const ref = useRef(null);
  const inView = useInView(ref, { triggerOnce: true, threshold: 0.1 });

  return (
    <div className="pt-28 pb-12 md:pt-32 md:pb-16 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6">
            <i className="ri-file-text-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Legal</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4">
            Terms of Use
          </h1>
          {/* <p className="text-base md:text-xl text-white/70 max-w-2xl mx-auto">
            Last updated: June 23, 2026
          </p> */}
        </motion.div>
      </div>
    </div>
  );
}

export default function TermsPage() {
  return (
    <PageLayout
      title="Terms of Use — Thoughtic Solutions"
      description="Read Thoughtic Solutions Terms of Use. Covers acceptance of terms, services, intellectual property, payment terms, confidentiality, limitation of liability, termination, and governing law for our custom software development and IT staffing services."
      keywords="Thoughtic Solutions terms of use, IT staffing terms, software development legal terms, terms and conditions, service agreement terms"
    >
      <PageHero />

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 leading-relaxed mb-10">
              Welcome to Thoughtic Solutions. These Terms of Use govern your access to and use of our website and services. Please read them carefully before using our platform or engaging our services.
            </p>

            <div className="space-y-10">
              {sections.map((section) => (
                <div key={section.id} id={section.id}>
                  <h2 className="text-xl md:text-2xl font-bold text-navy-900 mb-4">
                    {section.title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed">{section.content}</p>
                  {section.contact && (
                    <div className="mt-4 p-5 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 flex items-center justify-center bg-primary-50 rounded-lg flex-shrink-0">
                            <i className="ri-mail-line text-primary-600"></i>
                          </div>
                          <a href="mailto:info@thoughtic.com" className="text-primary-600 hover:underline text-sm font-medium">
                            info@thoughtic.com
                          </a>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 flex items-center justify-center bg-primary-50 rounded-lg flex-shrink-0">
                            <i className="ri-map-pin-line text-primary-600"></i>
                          </div>
                          <span className="text-sm text-gray-600">India</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <hr className="my-10 border-gray-200" />

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-sm text-gray-500">
                Have questions? <Link to="/trust-security" className="text-primary-600 hover:underline font-medium">Visit our Trust & Security page</Link> for more information.
              </p>
              <Link
                to="/privacy"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer group"
              >
                View Privacy Policy
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}